"use client"

import Link from "next/link"
import { useRouter } from "next/navigation"
import Image from "next/image"
import { Search, Menu, X, User, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useLanguage } from "@/lib/language-context"
import { useCurrency } from "@/lib/currency-context"
import { REGIONS, LANGUAGES } from "./header-client-selectors"
import { useState } from "react"
import dynamic from "next/dynamic"

import { ManufacturerFilter } from "@/components/manufacturer-filter"
import { RequestSparePartDialog } from "@/components/request-spare-part-dialog"

const HeaderClientSelectors = dynamic(
  () => import("./header-client-selectors").then((mod) => mod.HeaderClientSelectors),
  {
    ssr: false,
    loading: () => <div className="h-5 w-40 bg-primary/20 rounded animate-pulse" aria-hidden="true" />
  }
)

const HeaderClientCartStatus = dynamic(
  () => import("./header-client-cart-status").then((mod) => mod.HeaderClientCartStatus),
  {
    ssr: false,
    loading: () => <div className="h-9 w-48 bg-gray-100 rounded animate-pulse" aria-hidden="true" />
  }
)

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  const { language, setLanguage, t } = useLanguage()
  const { currency, setCurrency } = useCurrency()

  const regions = REGIONS
  const languages = LANGUAGES

  const toggleDropdown = (name: string) => {
    setOpenDropdown(openDropdown === name ? null : name)
  }

  const router = useRouter()
  const [searchQuery, setSearchQuery] = useState("")

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<SVGSVGElement>) => {
    if ((e.type === "keydown" && (e as React.KeyboardEvent).key === "Enter") || e.type === "click") {
      if (searchQuery.trim()) {
        router.push(`/products?search=${encodeURIComponent(searchQuery.trim())}`)
      }
    }
  }

  return (
    <header className="sticky top-0 z-50 bg-white border-b">
      {/* Top Bar - Hidden on mobile/tablet */}
      <div className="hidden lg:flex bg-primary text-primary-foreground px-4 py-1 justify-end gap-6 text-sm items-center h-8 relative z-20">
        <HeaderClientSelectors />
      </div>

      {/* Main Header */}
      <div className="w-full px-4 py-0 sm:px-6 lg:px-8 relative z-10">
        <div className="flex items-center justify-between gap-4">
          <Link href="/" className="flex items-center gap-2 flex-shrink-0 relative z-50">
            <Image
              src="/file.svg"
              alt="Unispare Logo"
              width={1024}
              height={524}
              className="h-12 sm:h-16 md:h-20 w-auto object-contain transition-transform hover:scale-105"
              priority
            />
          </Link>

          {/* Main Nav Items */}
          <nav className="hidden md:flex flex-1 items-center justify-end gap-12 mr-8">
            <ManufacturerFilter
              trigger={
                <div
                  role="button"
                  tabIndex={0}
                  className="text-base font-medium hover:text-primary transition-colors cursor-pointer select-none"
                >
                  {t("nav.manufacturers")}
                </div>
              }
            />

            {/* Products Dropdown */}
            <div className="relative group">
              <Link
                href="/products"
                className="text-base font-medium hover:text-primary transition-colors flex items-center gap-1 cursor-pointer select-none"
              >
                {t("nav.products")}
                <ChevronDown className="h-4 w-4" />
              </Link>
              <div className="absolute left-0 top-full hidden group-hover:block pt-2">
                <div className="bg-white border rounded shadow-lg py-2 min-w-56">
                  <Link href="/products" className="block px-4 py-2 hover:bg-gray-100 text-sm font-semibold">
                    {t("products.view_all")}
                  </Link>
                  <div className="border-t my-2"></div>
                  <Link href="/products?category=automation" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    Automation
                  </Link>
                  <Link href="/products?category=servo-motors" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    Servo Motors
                  </Link>
                  <Link href="/products?category=sensors" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    Sensors
                  </Link>
                  <Link href="/products?category=drive-technology" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    Drive Technology
                  </Link>
                  <Link href="/products?category=pneumatics" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    Pneumatics
                  </Link>
                  <Link href="/products?category=ball-bearing" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    Ball-Bearing
                  </Link>
                  <Link href="/products?category=pumps" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    Pumps
                  </Link>
                  <Link href="/products?category=valves" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    Valves
                  </Link>
                </div>
              </div>
            </div>

            <RequestSparePartDialog
              trigger={
                <button className="text-base font-medium hover:text-primary transition-colors cursor-pointer">
                  {t("nav.request_part")}
                </button>
              }
            />

            <Link href="/industrial-purchase" className="text-base font-medium hover:text-primary transition-colors">
              {t("nav.industrial_purchase")}
            </Link>

            {/* Company Details Dropdown */}
            <div className="relative group">
              <div
                role="button"
                tabIndex={0}
                className="text-base font-medium hover:text-primary transition-colors flex items-center gap-1 cursor-pointer select-none"
                onClick={() => toggleDropdown("company")}
              >
                {t("nav.company_details")}
                <ChevronDown className="h-4 w-4" />
              </div>
              <div className="absolute left-0 top-full hidden group-hover:block pt-2">
                <div className="bg-white border rounded shadow-lg py-2 min-w-48">
                  <Link href="/faq" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    {t("nav.faq")}
                  </Link>
                  <Link href="/reviews" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    {t("nav.reviews")}
                  </Link>
                  <Link href="/payment" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    {t("nav.payment")}
                  </Link>
                  <Link href="/returns" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    {t("nav.returns")}
                  </Link>
                  <Link href="/shipment" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    {t("nav.shipment")}
                  </Link>
                  <Link href="/conditions" className="block px-4 py-2 hover:bg-gray-100 text-sm">
                    {t("nav.conditions")}
                  </Link>
                </div>
              </div>
            </div>
          </nav>

          {/* Right Icons */}
          <div className="flex items-center gap-1 sm:gap-2 md:gap-4 relative z-50">
            {/* Search Bar (Desktop) */}
            <div className="hidden md:flex justify-end">
              <div className="flex items-center bg-muted/50 hover:bg-white focus-within:bg-white rounded-full px-4 border border-transparent hover:border-accent focus-within:border-primary focus-within:shadow-md transition-all duration-300 w-[360px] h-10 group">
                <Search
                  className="h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors flex-shrink-0 mr-2 cursor-pointer"
                  onClick={handleSearch}
                />
                <input
                  type="text"
                  placeholder={t("header.search")}
                  className="flex-1 bg-transparent outline-none text-base placeholder:text-gray-500 w-full"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearch}
                />
              </div>
            </div>

            {/* Mobile Search Icon */}
            <Button variant="ghost" size="icon" className="md:hidden" asChild>
              <Link href="/products" aria-label={t("header.search_products")}>
                <Search className="h-5 w-5" />
              </Link>
            </Button>

            {/* Contact & FAQ */}
            <div className="hidden md:flex items-center gap-4">
              <Link
                href="/contact"
                className="text-base font-medium hover:text-primary transition-colors flex items-center gap-1"
              >
                📞 {t("nav.contact")}
              </Link>
              <Link
                href="/faq"
                className="text-base font-medium hover:text-primary transition-colors flex items-center gap-1"
              >
                ❓ {t("nav.faq")}
              </Link>
            </div>
            {/* User Account */}
            <Button variant="ghost" size="icon" asChild>
              <Link href="/account" aria-label={t("header.user_account")}>
                <User className="h-5 w-5" />
              </Link>
            </Button>

            <HeaderClientCartStatus />

            {/* Mobile Menu */}
            <button 
              className="md:hidden" 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? t("header.close_menu") : t("header.open_menu")}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div id="mobile-menu" className="md:hidden border-t bg-white">
          <nav className="px-4 pb-4 pt-2 space-y-4">
            <ManufacturerFilter
              trigger={<button className="block text-sm font-medium py-2 w-full text-left">{t("nav.manufacturers")}</button>}
            />
            <button
              onClick={() => toggleDropdown("products")}
              className="w-full text-left text-sm font-medium py-2 flex justify-between items-center"
            >
              {t("nav.products")}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${openDropdown === "products" ? "rotate-180" : ""}`}
              />
            </button>
            {openDropdown === "products" && (
              <div className="ml-4 space-y-2">
                <Link href="/products" className="block text-sm font-semibold py-1">
                  {t("products.view_all")}
                </Link>
                <Link href="/products?category=automation" className="block text-sm py-1">
                  Automation
                </Link>
                <Link href="/products?category=servo-motors" className="block text-sm py-1">
                  Servo Motors
                </Link>
                <Link href="/products?category=sensors" className="block text-sm py-1">
                  Sensors
                </Link>
                <Link href="/products?category=drive-technology" className="block text-sm py-1">
                  Drive Technology
                </Link>
                <Link href="/products?category=pneumatics" className="block text-sm py-1">
                  Pneumatics
                </Link>
                <Link href="/products?category=ball-bearing" className="block text-sm py-1">
                  Ball-Bearing
                </Link>
                <Link href="/products?category=pumps" className="block text-sm py-1">
                  Pumps
                </Link>
                <Link href="/products?category=valves" className="block text-sm py-1">
                  Valves
                </Link>
              </div>
            )}
            <RequestSparePartDialog
              trigger={
                <button 
                  className="block text-sm font-medium py-2 text-left w-full cursor-pointer"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("nav.request_part")}
                </button>
              }
            />
            <Link href="/industrial-purchase" className="block text-sm font-medium py-2">
              {t("nav.industrial_purchase")}
            </Link>
            <button
              onClick={() => toggleDropdown("company")}
              className="w-full text-left text-sm font-medium py-2 flex justify-between items-center"
            >
              {t("nav.company_details")}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${openDropdown === "company" ? "rotate-180" : ""}`}
              />
            </button>
            {openDropdown === "company" && (
              <div className="ml-4 space-y-2">
                <Link href="/faq" className="block text-sm py-1">
                  {t("nav.faq")}
                </Link>
                <Link href="/reviews" className="block text-sm py-1">
                  {t("nav.reviews")}
                </Link>
                <Link href="/payment" className="block text-sm py-1">
                  {t("nav.payment")}
                </Link>
                <Link href="/returns" className="block text-sm py-1">
                  {t("nav.returns")}
                </Link>
                <Link href="/shipment" className="block text-sm py-1">
                  {t("nav.shipment")}
                </Link>
                <Link href="/conditions" className="block text-sm py-1">
                  {t("nav.conditions")}
                </Link>
              </div>
            )}
            <Link href="/contact" className="block text-sm font-medium py-2">
              {t("nav.contact")}
            </Link>
            <Link href="/faq" className="block text-sm font-medium py-2">
              {t("nav.faq")}
            </Link>

            {/* Mobile Region & Language */}
            <div className="pt-4 border-t space-y-4">
              <button
                onClick={() => toggleDropdown("mobile-region")}
                className="w-full text-left text-sm font-medium py-2 flex justify-between items-center"
              >
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">Region:</span>
                  {regions.find((r) => r.value === currency)?.name}
                </span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${openDropdown === "mobile-region" ? "rotate-180" : ""}`}
                />
              </button>
              {openDropdown === "mobile-region" && (
                <div className="ml-4 grid grid-cols-1 gap-2">
                  {regions.map((region) => (
                    <button
                      key={region.value}
                      onClick={() => {
                        setCurrency(region.value)
                        setOpenDropdown(null)
                      }}
                      className={`text-left px-3 py-2 rounded-md text-sm ${currency === region.value ? "bg-primary/10 font-bold text-primary" : "text-gray-600"}`}
                    >
                      {region.name}
                    </button>
                  ))}
                </div>
              )}

              <button
                onClick={() => toggleDropdown("mobile-language")}
                className="w-full text-left text-sm font-medium py-2 flex justify-between items-center"
              >
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">Language:</span>
                  {languages.find((l) => l.value === language)?.name}
                </span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${openDropdown === "mobile-language" ? "rotate-180" : ""}`}
                />
              </button>
              {openDropdown === "mobile-language" && (
                <div className="ml-4 grid grid-cols-2 gap-2">
                  {languages.map((l) => (
                    <button
                      key={l.value}
                      onClick={() => {
                        setLanguage(l.value)
                        setOpenDropdown(null)
                      }}
                      className={`text-left px-3 py-2 rounded-md text-sm ${language === l.value ? "bg-primary/10 font-bold text-primary" : "text-gray-600"}`}
                    >
                      {l.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>
        </div>
      )}

      <Link href="/shipment" className="block hidden sm:block">
        <div className="bg-primary text-primary-foreground text-center py-1 hover:bg-primary/90 transition-colors cursor-pointer">
          <p className="text-sm font-medium">{t("banner.shipping")}</p>
        </div>
      </Link>
    </header>
  )
}
